import React, { memo, useState } from 'react';
import { getPlayerDisplayName } from '../utils/playerUtils';
import Button from './shared/Button';
import './Scoreboard.css';

const Scoreboard = memo(
  ({
    players,
    scores,
    isRoomCreator,
    isDeputy,
    username,
    wageringPlayers,
    judge,
    roomCreatorPlayerId,
    deputyPlayerId,
    coHostEnabled,
    designatedFirstCzarId,
    currentRound,
    onKickPlayer,
    onRemoveBot,
    onDemoteToSpectator,
    onDesignateDeputy,
    onPromoteSelf,
    rhpDoubleAwardEnabled,
    doubleAwardUsedMap,
  }) => {
    const [spectatorsExpanded, setSpectatorsExpanded] = useState(false);
    // #78: default collapsed on mobile, where this sidebar competes for
    // vertical space with the judge/hand area below it; expanded by default
    // on desktop, which has a dedicated side column with room to spare.
    const [playersExpanded, setPlayersExpanded] = useState(
      () => typeof window !== 'undefined' && window.innerWidth > 768
    );

    if (!players.length) return null;

    // Separate active players and spectators
    const activePlayers = players.filter((p) => !p.isSpectator);
    const spectators = players.filter((p) => p.isSpectator);

    if (!activePlayers.length && !spectators.length) return null;

    const sortedPlayers = [...activePlayers].sort((a, b) => {
      const scoreA = scores[a?.id] || scores[a?.socketId] || 0;
      const scoreB = scores[b?.id] || scores[b?.socketId] || 0;
      return scoreB - scoreA;
    });

    // Debug kick button visibility - enhanced logging
    console.log('🔍🎯 KICK BUTTON VISIBILITY CHECK:', {
      isRoomCreator: isRoomCreator,
      isRoomCreatorType: typeof isRoomCreator,
      username: username,
      roomCreatorPlayerId: roomCreatorPlayerId,
      players: sortedPlayers.map((p) => ({
        username: p?.username,
        id: p?.id,
        playerId: p?.playerId,
        socketId: p?.socketId,
        isBot: p?.isBot,
        willShowKickButton:
          isRoomCreator && !p?.isBot && p?.username !== username && !!(p?.id || p?.playerId),
      })),
    });

    return (
      <div className="scoreboard">
        <button
          className="scoreboard-toggle"
          onClick={() => setPlayersExpanded(!playersExpanded)}
          aria-expanded={playersExpanded}
          aria-label={`${playersExpanded ? 'Collapse' : 'Expand'} scoreboard`}
        >
          <h3>Scoreboard</h3>
          <span className="toggle-arrow">{playersExpanded ? '▼' : '▶'}</span>
        </button>
        {playersExpanded && (
          <div className="score-list">
            {sortedPlayers.map((player, index) => {
              const isWageringPlayer = wageringPlayers.some(
                (w) => w?.username === player?.username
              );
              const isFirstCzar =
                currentRound === 1 &&
                designatedFirstCzarId &&
                (player?.id === designatedFirstCzarId ||
                  player?.playerId === designatedFirstCzarId);
              const playerDisplayName = getPlayerDisplayName(
                player,
                roomCreatorPlayerId,
                judge?.username,
                deputyPlayerId
              );

              const isDisconnected = player?.connected === false;

              // RHP: Judge Double Award — show indicator on the current Judge if their
              // once-per-game power is still available
              const isCurrentJudge =
                judge && (player?.id === judge.id || player?.username === judge.username);
              const hasDoubleAwardAvailable =
                rhpDoubleAwardEnabled &&
                isCurrentJudge &&
                !player?.isBot &&
                !doubleAwardUsedMap?.[player?.id];

              return (
                <div
                  key={player?.id || player?.socketId || index}
                  className={`score-item ${isDisconnected ? 'disconnected' : ''}`}
                >
                  <span className={`player-name ${isWageringPlayer ? 'wagering-player' : ''}`}>
                    {playerDisplayName}
                    {hasDoubleAwardAvailable && (
                      <span className="double-award-indicator" title="Double Award available">
                        2x
                      </span>
                    )}
                    {isDisconnected && <span className="disconnected-indicator"> 🔌</span>}
                  </span>
                  {isWageringPlayer && <span className="wagering-indicator"> 💰</span>}
                  {isFirstCzar && <span className="poop-rule-indicator"> 💩</span>}
                  <div className="score-actions">
                    <span className="score">
                      {scores[player?.id] || scores[player?.socketId] || 0}
                    </span>
                    {/* Remove bot button - for host/deputy to remove bots */}
                    {(isRoomCreator || isDeputy) &&
                      player?.isBot &&
                      (player?.id || player?.playerId) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="remove-bot-button"
                          onClick={() => {
                            const confirmRemove = window.confirm(
                              `Remove ${player.username} 🤖 from the room?`
                            );
                            if (confirmRemove && onRemoveBot) {
                              console.log('🗑️ DEBUG: Removing bot:', player.username);
                              console.log('🗑️ DEBUG: Bot ID:', player.id || player.playerId);
                              onRemoveBot(player.id || player.playerId, player.username);
                            }
                          }}
                          ariaLabel="Remove bot"
                        >
                          🗑️
                        </Button>
                      )}
                    {/* Designate deputy button - for room creator only during active game */}
                    {coHostEnabled &&
                      isRoomCreator &&
                      !player?.isBot &&
                      player?.username !== username &&
                      (player?.id || player?.playerId) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`designate-deputy-button ${(player?.id || player?.playerId) === deputyPlayerId ? 'designated' : ''}`}
                          onClick={() => {
                            if (onDesignateDeputy) {
                              console.log('🎖️ DEBUG: Designating deputy:', player.username);
                              console.log('🎖️ DEBUG: Player ID:', player.id || player.playerId);
                              onDesignateDeputy(player.id || player.playerId);
                            }
                          }}
                          ariaLabel={
                            (player?.id || player?.playerId) === deputyPlayerId
                              ? 'Current Co-Host'
                              : 'Designate as Co-Host'
                          }
                        >
                          🎖️
                        </Button>
                      )}
                    {/* Demote to spectator button - for host/deputy to demote active players */}
                    {(isRoomCreator || isDeputy) &&
                      !player?.isBot &&
                      player?.username !== username &&
                      (player?.id || player?.playerId) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="demote-button"
                          title={`Move ${player.username} to voyeur — they can watch but not participate.`}
                          onClick={() => {
                            const confirmDemote = window.confirm(
                              `Move ${player.username} to voyeur? They will be able to watch the game but not participate.`
                            );
                            if (confirmDemote && onDemoteToSpectator) {
                              console.log('⬇️ DEBUG: Demoting player:', player.username);
                              console.log('⬇️ DEBUG: Player ID:', player.id || player.playerId);
                              onDemoteToSpectator(player.id || player.playerId, player.username);
                            }
                          }}
                          ariaLabel="Move to voyeur"
                        >
                          ⬇️
                        </Button>
                      )}
                    {/* Kick player button - for host/deputy to kick human players */}
                    {(isRoomCreator || isDeputy) &&
                      !player?.isBot &&
                      player?.username !== username &&
                      (player?.id || player?.playerId) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="kick-button"
                          onClick={() => {
                            console.log('🔍 DEBUG: Kicking player:', player.username);
                            console.log('🔍 DEBUG: Player ID:', player.id || player.playerId);
                            onKickPlayer(player.id || player.playerId, player.username);
                          }}
                          ariaLabel="Kick player"
                        >
                          ✖
                        </Button>
                      )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Spectators Section (Collapsible) */}
        {spectators.length > 0 && (
          <div className="spectators-section">
            <button
              className="spectators-toggle"
              onClick={() => setSpectatorsExpanded(!spectatorsExpanded)}
              aria-expanded={spectatorsExpanded}
              aria-label={`${spectatorsExpanded ? 'Collapse' : 'Expand'} voyeurs list`}
            >
              <span className="toggle-arrow">{spectatorsExpanded ? '▼' : '▶'}</span>
              <span className="spectators-header">👁️ Voyeurs ({spectators.length})</span>
            </button>

            {spectatorsExpanded && (
              <div className="spectators-list">
                {spectators.map((spectator, index) => {
                  const playerDisplayName = getPlayerDisplayName(
                    spectator,
                    roomCreatorPlayerId,
                    judge?.username,
                    deputyPlayerId
                  );
                  const isDisconnected = spectator?.connected === false;
                  const isCurrentUser = spectator?.username === username;

                  return (
                    <div
                      key={spectator?.id || spectator?.socketId || index}
                      className={`spectator-item ${isDisconnected ? 'disconnected' : ''}`}
                    >
                      <span className="spectator-name">
                        {playerDisplayName}
                        {isDisconnected && <span className="disconnected-indicator"> 🔌</span>}
                      </span>
                      {/* Show "Join Game" button for current user only (not bots) */}
                      {isCurrentUser && !spectator?.isBot && onPromoteSelf && (
                        <Button
                          onClick={onPromoteSelf}
                          variant="primary"
                          size="small"
                          className="join-game-button"
                        >
                          Join Game
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

export default Scoreboard;
